import PropTypes from "prop-types"
import React from "react"
import { twMerge } from "tailwind-merge"

import Typography from "../../../components/shared/Typography"
import { ScrollReveal, ScrollRevealItem } from "../../WebsiteBuilderV2/components/shared/ScrollReveal"
import { CARD_IMAGE_HOVER_CLASSES } from "../../WebsiteBuilderV2/components/shared/scrollRevealVariants"

const ExploreTherapiesSection = ({ therapyCategories = [], isMobile = false }) => {
  // Use therapy categories from database (passed as prop from backend)
  const allTherapies = therapyCategories

  const therapyImages = isMobile
    ? [
        { src: "/images/therapy-category-1.png", alt: "Holistic therapy session" },
        { src: "/images/therapy-category-2.png", alt: "Energy healing therapy" }
      ]
    : [
        { src: "/images/therapy-category-1.png", alt: "Holistic therapy session" },
        { src: "/images/therapy-category-2.png", alt: "Energy healing therapy" },
        { src: "/images/therapy-category-3.png", alt: "Massage therapy treatment" }
      ]

  return (
    <>
      <ScrollReveal
        as="section"
        variant="fadeIn"
        threshold={0.08}
        className={`w-full ${isMobile ? "bg-white py-1" : "bg-white pt-14"}`}>
        <div className={`container mx-auto ${isMobile ? "w-full px-6" : "max-w-splash-container px-5"}`}>
          <div className={`mb-10 flex gap-12 ${isMobile ? "flex-col gap-8" : "md:flex-col"}`}>
            <ScrollRevealItem
              staggerIndex={0}
              variant="fadeUp"
              className={`flex flex-col gap-4 ${isMobile ? "w-full gap-6" : "max-w-[416px]"}`}>
              <div className={`flex flex-col ${isMobile ? "gap-3" : "gap-6"}`}>
                <Typography variant="h3" as="h3" className="font-serif text-black">
                  Explore over 100+ holistic therapies
                </Typography>
                <Typography variant="body" className="text-gray-dark">
                  From massage therapists to reiki masters, nutritionists to naturopaths, and everything in between.
                </Typography>
              </div>
              <a
                href="/practitioner-search"
                className={`w-fit rounded bg-teal text-white ${isMobile ? "px-4 py-0" : "px-6 py-3"}`}
                style={
                  isMobile ? { height: "48px", display: "flex", alignItems: "center", justifyContent: "center" } : {}
                }>
                <Typography variant="body" className="font-bold text-white">
                  {isMobile ? "Browse therapies" : "Browse Therapies"}
                </Typography>
              </a>
            </ScrollRevealItem>

            <div className={`flex flex-1 ${isMobile ? "gap-[14.559px]" : "gap-4"}`}>
              {therapyImages.map((image, index) => (
                <ScrollRevealItem
                  key={image.src}
                  staggerIndex={index + 1}
                  variant="scaleIn"
                  className={twMerge(
                    "group h-[256px] flex-1 overflow-hidden rounded-2xl bg-gray-light",
                    !isMobile && "h-64"
                  )}>
                  <img
                    src={image.src}
                    alt={image.alt}
                    className={twMerge("size-full object-cover", CARD_IMAGE_HOVER_CLASSES)}
                  />
                </ScrollRevealItem>
              ))}
            </div>
          </div>

          <div className={`border-t border-gray-light ${isMobile ? "my-8" : "mb-0"}`} />
        </div>
      </ScrollReveal>

      {isMobile ? (
        <ScrollReveal as="section" variant="fadeIn" threshold={0.08} className="relative w-full bg-white">
          <div className="container mx-auto w-full px-6">
            <div className="relative flex flex-col gap-2 pb-14 pt-0">
              <div className="flex flex-col gap-2">
                {allTherapies.slice(0, 12).map((therapy, index) => (
                  <ScrollRevealItem key={therapy} staggerIndex={index + 1} variant="fadeUp">
                    <a
                      href={`/practitioner-search?specialty=${encodeURIComponent(therapy)}`}
                      className="text-[16px] leading-[24px] text-gray-dark transition-colors hover:font-bold hover:text-black hover:underline"
                      style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}>
                      {therapy}
                    </a>
                  </ScrollRevealItem>
                ))}
              </div>
              {/* Gradient fade and button */}
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[176px]"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, rgb(255, 255, 255, 0.3) 0%, rgb(255, 255, 255, 0.95) 40%, rgb(255, 255, 255) 70%)"
                }}
              />
              <div className="relative z-10 flex justify-center pt-4">
                <a
                  href="/practitioner-search"
                  className="rounded border border-gray bg-white px-4 py-1 text-black transition-colors hover:bg-gray-light">
                  <Typography variant="body" className="font-bold text-black">
                    View all services
                  </Typography>
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      ) : (
        <ScrollReveal as="section" variant="fadeIn" threshold={0.08} className="w-full bg-white pb-24 pt-10">
          <div className="container mx-auto max-w-splash-container px-5">
            <div className="grid grid-cols-4 gap-2 md:grid-cols-2 sm:grid-cols-1">
              {allTherapies.map((therapy, index) => (
                <ScrollRevealItem key={therapy} staggerIndex={(index % 12) + 1} variant="fadeUp">
                  <a
                    href={`/practitioner-search?specialty=${encodeURIComponent(therapy)}`}
                    className="text-gray-dark transition-colors hover:font-bold hover:text-black hover:underline"
                    style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}>
                    <Typography variant="body">{therapy}</Typography>
                  </a>
                </ScrollRevealItem>
              ))}
            </div>
          </div>
        </ScrollReveal>
      )}
    </>
  )
}

ExploreTherapiesSection.propTypes = {
  therapyCategories: PropTypes.arrayOf(PropTypes.string),
  isMobile: PropTypes.bool
}

export default ExploreTherapiesSection
